import React, { useState, useRef, useEffect } from "react";
import { useDrag, useDrop } from "react-dnd";
import { Box, Icon, MenuItem } from "@showitinc/showit-wc";
import cx from "classnames";

import {
  generateDragSpec,
  generateDropSpec,
  getBorderSide,
  getTargetArea,
} from "../../../utils/DnDUtils";
import ContextMenuWrapper from "../../ContextMenus/ContextMenuWrapper";
import openRowContextMenu from "../../../utils/openRowContextMenu";
import {
  folderHeaderDrop,
  sitePanelDrop,
} from "../../../components/sitepanel/SitePanelDropHandlers";
import AppConstants from "../../../constants/AppConstants";
import DocumentStore from "../../../document/DocumentStore";
import { COMMANDS } from "../../../document/commands";
import UIActions from "../../../actions/UIActions";
import styles from "./SitePanelFolder.module.css";
import { SITE_PAGE_ITEM, SITE_PAGE_FOLDER } from "../../../constants/DnDConstants";

/**
 * Pages Panel Item
 * Reusable item component for pages, wordpress templaces, site canvases, and canvas sets
 * @component
 * @param {object} props
 * @param {React.ReactNode} props.children - The context menu for the dropdown
 * @param {string} props.id - The id of the item
 * @param {Immutable.Map} props.source - The item object repping the page, block, or canvas set
 * @param {number} props.index - Order index of the item the list
 * @param {number} props.listIndex - Order index of the list
 * @param {function} props.onSelect - Callback when the item is selected
 * @returns {React.ReactElement}
 */

const { ITEM_TYPE_PAGE_FOLDER, DROP_SHADOW_TOP, DROP_SHADOW_BOTTOM } = AppConstants;

const SitePanelFolder = ({ children, dataIndex, id, listIndex, source, measure, style = {} }) => {
  const [open, setOpen] = useState(false);
  const [dropArea, setDropArea] = useState("");
  const [headerDropArea, setHeaderDropArea] = useState();
  const wrapperRef = useRef();
  const selfRef = useRef();
  const headerRef = useRef();

  const name = source.get("name");
  const index = source.get("index");
  const slug = source.get("slug");
  const blog = !!source.get("wp");

  const drag = generateDragSpec({
    type: SITE_PAGE_FOLDER,
    item: {
      id,
      index,
      listIndex,
      type: ITEM_TYPE_PAGE_FOLDER,
      blog,
    },
    canDrag: () => !open,
    onDrop: ({ item, target }) =>
      DocumentStore.dispatch({ type: COMMANDS.MOVE_ITEM, payload: [item, target] }),
  });

  const drop = generateDropSpec({
    type: [SITE_PAGE_FOLDER, SITE_PAGE_ITEM],
    canDrop: (item) => item?.blog === blog,
    onHover: (monitor) => setDropArea(getTargetArea(monitor, selfRef.current)),
    onDrop: sitePanelDrop({
      id,
      index,
      listIndex,
      dropRef: selfRef.current,
      type: ITEM_TYPE_PAGE_FOLDER,
      dropIn: false,
    }),
  });

  const headerDrop = generateDropSpec({
    type: SITE_PAGE_ITEM,
    canDrop: (item) => item?.blog === blog,
    // disabled: open && !!children.length,
    onHover: (monitor) => setHeaderDropArea(getTargetArea(monitor, headerRef.current)),
    onDrop: folderHeaderDrop({ id, index, listIndex, dropRef: selfRef.current }),
  });

  const [{ isDragging }, dragRef] = useDrag(drag);
  const [{ isOverCurrent, canDrop }, dropRef] = useDrop(drop);
  const [{ isOver: isOverHeader, canDrop: canDropHeader }, headerDropRef] = useDrop(headerDrop);

  // todo: folder specific
  const _rename = (event) => {
    const { target } = event;
    // todo: is this still necessary?
    if (target?.closest(".no-autoclose")) {
      return;
    }

    UIActions.toggleRenamePopup({
      title: ITEM_TYPE_PAGE_FOLDER,
      itemType: ITEM_TYPE_PAGE_FOLDER,
      itemId: id,
      name,
      slug,
    });
  };

  const { ...inlineStyles } = style;
  const wrapperStyles = {};

  if (
    canDrop &&
    isOverCurrent &&
    !isDragging &&
    (!open || !children.length ? !isOverHeader : true)
  ) {
    wrapperStyles.boxShadow = dropArea === "top" ? DROP_SHADOW_TOP : DROP_SHADOW_BOTTOM;
  }

  if (canDropHeader && isOverHeader) {
    inlineStyles.borderColor = "var(--background-accent-primary)";
  }

  const _toggleOpen = (e) => {
    const validClick = [styles.root, styles["item-label"], styles.icon].some((className) =>
      e.target.classList.contains(className)
    );
    validClick && setOpen((prev) => !prev);
  };

  useEffect(() => {
    wrapperRef.current && measure(wrapperRef.current);
  }, [open]);

  return (
    <div
      data-index={dataIndex}
      className={styles.wrapper}
      style={wrapperStyles}
      ref={(wrapper) => {
        wrapperRef.current = wrapper;
        measure(wrapper);
      }}
    >
      <div
        className={cx(styles.root, { [styles.open]: open })}
        onClick={_toggleOpen}
        onContextMenu={(e) => openRowContextMenu({ event: e, rowEl: selfRef.current, active: true })}
        style={inlineStyles}
        ref={(self) => {
          selfRef.current = self;
          dragRef(self);
          dropRef(self);
        }}
      >
        <div className={styles.item}>
          <div
            className={styles.header}
            ref={(self) => {
              headerRef.current = self;
              headerDropRef(self);
            }}
          >
            <Icon
              type={open ? "folderOpen" : "folderClosed"}
              size="20"
              className={cx(styles.icon)}
            />
            <span className={styles["item-label"]}>{name}</span>

            <div className={styles["menu-container"]}>
              <ContextMenuWrapper icon="menuMoreHoriz" active>
                <MenuItem onClick={_rename}>Rename</MenuItem>
                <MenuItem
                  className={styles.delete}
                  onClick={() =>
                    DocumentStore.dispatch({ type: COMMANDS.REMOVE_FOLDER, payload: { id } })
                  }
                >
                  <Box className={styles.delete}>Delete</Box>
                </MenuItem>
              </ContextMenuWrapper>
            </div>
          </div>
          {open ? children : null}
        </div>
      </div>
    </div>
  );
};

export default SitePanelFolder;
